import {
  Suspense,
  lazy,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useBookingForm } from "../../hooks/useBookingForm";
import { usePostBookingFormMutation } from "../../services/common.service";
import type {
  ApiBookingPayload,
  BookingForm as BookingFormType,
  LessonDate,
} from "../../types/booking";
import type { FieldErrors } from "react-hook-form";
import { formatDateLocal } from "../../utils/helpers";
import { isDateTooSoon } from "../../utils/validation";
import StepIndicator from "./StepIndicator";
import WarningBanner from "./WarningBanner";
import BookingPersonForm from "./BookingPersonForm";
import LessonRequestForm from "./LessonRequestForm";
import BookingSummary from "./BookingSummary";
import SuccessPage from "./SuccessPage";
const HelpModal = lazy(() => import("./HelpModal"));
const ReCAPTCHA = lazy(() =>
  import("react-google-recaptcha").then((m) => ({ default: m.default })),
);
import type ReCAPTCHAType from "react-google-recaptcha";
import { RECAPTCHA_SITE_KEY } from "../../constants/constant";

const STEPS = ["Booking Details", "Lesson Requests", "Review & Submit"];

const BookingForm = () => {
  const [showHelp, setShowHelp] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showInstructorInfoModal, setShowInstructorInfoModal] = useState(false);
  const [hasShownInstructorModal, setHasShownInstructorModal] = useState(false);
  const bookingMutation = usePostBookingFormMutation();
  const recaptchaRef = useRef<ReCAPTCHAType>(null);

  const {
    form,
    currentStep,
    setCurrentStep,
    lessonDates,
    addParticipant,
    removeParticipant,
    addLessonDate,
    removeLessonDate,
    updateLessonDate,
    updateParticipant,
    resetBookingForm,
  } = useBookingForm();

  useEffect(() => {
    const onLastStep = currentStep === STEPS.length - 1;

    if (onLastStep) {
      // On entering submit step, reset widget and clear token without surfacing an error yet
      recaptchaRef.current?.reset();
      form.setValue("captchaToken", "", { shouldValidate: false });
      form.clearErrors("captchaToken");
    } else {
      // Leaving submit step or on other steps: ensure no stale token remains
      form.setValue("captchaToken", "", { shouldValidate: false });
      form.clearErrors("captchaToken");
    }
  }, [currentStep, form]);

  // Helper function to transform form data to API payload
  const transformToApiPayload = useCallback(
    (data: BookingFormType, captchaToken: string): ApiBookingPayload => {
      return {
        captchaToken,
        name: data.bookingPerson.name,
        email: data.bookingPerson.email,
        confirm_email: data.bookingPerson.confirmEmail,
        hotel_id: Number(data.bookingPerson.hotelId),
        comments: data.bookingPerson.comments || undefined,
        agent_id: data.bookingPerson.agentId
          ? Number(data.bookingPerson.agentId)
          : undefined,
        ref_no: data.bookingPerson.referenceNo || undefined,
        participant: data.lessonDates.map((ld) => {
          if (!ld.date) {
            throw new Error("Lesson date is required before submission.");
          }
          return {
            booking_date: formatDateLocal(ld.date),
            member: ld.participants.map((p) => ({
              name: p.name,
              age: p.age ? Number(p.age) : undefined,
              skisnowboard:
                p.activity.toLowerCase() === "snowboard" ? "snowboard" : "ski",
              lesson_code_id: Number(p.lessonType) || 0,
              ampm:
                p.timeSlot.toLowerCase() === "pm"
                  ? "pm"
                  : p.timeSlot.toLowerCase() === "fd"
                    ? "fd"
                    : "am",
              ski_level: p.skillLevel,
              lesson_type: p.lessonType,
            })),
          };
        }),
      };
    },
    [],
  );

  // Helper function to handle captcha errors
  const handleCaptchaError = useCallback(() => {
    form.setError("captchaToken", {
      type: "manual",
      message: "Captcha expired. Please try again",
    });
    recaptchaRef.current?.reset();
    form.setValue("captchaToken", "");
  }, [form]);

  const onSubmit = async (data: BookingFormType) => {
    // Safety net: block submission if any lesson date is too soon
    const tooSoonIndex = data.lessonDates.findIndex(
      (ld) => ld.date && isDateTooSoon(ld.date),
    );
    if (tooSoonIndex !== -1) {
      form.setError(`lessonDates.${tooSoonIndex}.date`, {
        type: "manual",
        message:
          "Online bookings must be made at least 2 days in advance. Please select a later date or contact us directly.",
      });
      return;
    }

    let captcha = form.getValues("captchaToken") ?? "";
    if (!captcha) {
      try {
        const token = await recaptchaRef.current?.executeAsync();
        if (token) {
          captcha = token;
          form.setValue("captchaToken", token, { shouldValidate: true });
          form.clearErrors("captchaToken");
        } else {
          form.setError("captchaToken", {
            type: "manual",
            message: "Captcha is required",
          });
          return;
        }
      } catch {
        form.setError("captchaToken", {
          type: "manual",
          message: "Captcha failed to execute. Please try again",
        });
        return;
      }
    }

    const payload = transformToApiPayload(data, captcha);

    try {
      const result = await bookingMutation.mutateAsync(payload);
      if (
        result &&
        typeof result === "object" &&
        "success" in result &&
        result.success !== false
      ) {
        recaptchaRef.current?.reset();
        form.setValue("captchaToken", "");
        setIsSubmitted(true);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message.toLowerCase() : "";
      if (
        message.includes("captcha") ||
        message.includes("recaptcha") ||
        message.includes("expired")
      ) {
        handleCaptchaError();
        return;
      }
      throw error;
    }
  };

  const nextStep = async () => {
    let isValid = false;

    if (currentStep === 0) {
      isValid = await form.trigger("bookingPerson");
    } else if (currentStep === 1) {
      // Validate lesson dates and participants
      isValid = await form.trigger("lessonDates");
    } else {
      // On the final step, validate everything except captcha first
      isValid = await form.trigger(
        ["bookingPerson", "lessonDates", "acceptedTerms"],
        {
          shouldFocus: false,
        },
      );
    }

    if (!isValid) {
      // Scroll to first error within the current step only
      if (currentStep === 1) {
        const container = document.querySelector(
          "#lesson-requests-section",
        ) as HTMLElement | null;
        const firstError = container?.querySelector(
          ".text-red-600",
        ) as HTMLElement | null;
        if (firstError) {
          const field = (firstError.previousElementSibling?.querySelector?.(
            "input,select,textarea",
          ) ||
            firstError.parentElement?.querySelector?.(
              "input,select,textarea",
            )) as HTMLElement | null;
          if (field) {
            field.scrollIntoView({ behavior: "smooth", block: "center" });
            // Small timeout to ensure scroll completes before focusing
            setTimeout(() => field.focus(), 50);
          } else {
            firstError.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      }
      return;
    }

    if (currentStep < STEPS.length - 1) {
      if (currentStep === 1 && !hasShownInstructorModal) {
        const totalParticipants = form
          .getValues("lessonDates")
          .reduce((sum, ld) => sum + ld.participants.length, 0);
        if (totalParticipants > 1) {
          setShowInstructorInfoModal(true);
          setHasShownInstructorModal(true);
          return;
        }
      }
      setCurrentStep(currentStep + 1);
    } else {
      try {
        // Always reset to ensure we get a fresh token (handles prior expiry)
        recaptchaRef.current?.reset();
        // Execute Invisible reCAPTCHA and set token before final validation/submit
        const token = await recaptchaRef.current?.executeAsync();
        if (!token) {
          form.setError("captchaToken", {
            type: "manual",
            message: "Captcha is required",
          });
          return;
        }
        form.setValue("captchaToken", token, { shouldValidate: false });
        form.clearErrors("captchaToken");
      } catch {
        form.setError("captchaToken", {
          type: "manual",
          message: "Captcha failed to execute. Please try again",
        });
        return;
      }

      // Now run the regular submit which will pass validation with the token set
      form.handleSubmit(onSubmit)();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onNewBooking = () => {
    resetBookingForm();
    setIsSubmitted(false);
  };
  if (isSubmitted) {
    return <SuccessPage onNewBooking={onNewBooking} />;
  }

  const handleCaptchaChange = (token: string | null) => {
    if (token) {
      form.setValue("captchaToken", token ?? "", { shouldValidate: true });
      form.clearErrors("captchaToken");
    } else {
      form.setValue("captchaToken", "", { shouldValidate: true });
      form.setError("captchaToken", {
        type: "manual",
        message: "Captcha is required",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <main className="max-w-7xl mx-auto px-4 py-8 text-left">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white shadow-xl overflow-hidden rounded-[6px]"
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6">
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-2xl font-bold text-white"
            >
              Book Your Snow Sports Lesson
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-blue-100 mt-2"
            >
              Fill out the form below to secure your spot on the slopes
            </motion.p>
          </div>

          <div className="p-4 sm:p-6 md:p-8">
            <StepIndicator currentStep={currentStep} steps={STEPS} />

            <WarningBanner />

            <form
              onSubmit={form.handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  // Prevent accidental submits when pressing Enter inside inputs
                  e.preventDefault();
                }
              }}
              className="space-y-8"
            >
              <AnimatePresence mode="wait">
                {currentStep === 0 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    <BookingPersonForm form={form} />
                  </motion.div>
                )}

                {currentStep === 1 && (
                  <motion.div
                    key="step2"
                    id="lesson-requests-section"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    <LessonRequestForm
                      lessonDates={lessonDates}
                      onAddParticipant={addParticipant}
                      onRemoveParticipant={removeParticipant}
                      onAddLessonDate={addLessonDate}
                      onRemoveLessonDate={removeLessonDate}
                      onUpdateLessonDate={updateLessonDate}
                      onUpdateParticipant={updateParticipant}
                      register={form.register}
                      errors={
                        form.formState.errors.lessonDates as FieldErrors<{
                          lessonDates: LessonDate[];
                        }>["lessonDates"]
                      }
                    />
                  </motion.div>
                )}

                {currentStep === 2 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    <BookingSummary form={form} lessonDates={lessonDates} />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center pt-6 border-t">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {currentStep > 0 ? (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="px-6 py-3 bg-red-500 text-white border border-red-500 rounded-lg hover:bg-red-600 transition-colors"
                    >
                      Previous
                    </button>
                  ) : (
                    <div></div>
                  )}
                </motion.div>
                <div className="flex flex-col gap-2">
                  {/* Hidden field to bind captchaToken to form for validation */}
                  <input
                    type="hidden"
                    value={form.watch("captchaToken")}
                    readOnly
                  />
                  <Suspense fallback={<div className="h-[78px]" />}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleCaptchaChange}
                      size="invisible"
                      badge="bottomright"
                      onExpired={() => {
                        form.setValue("captchaToken", "", {
                          shouldValidate: true,
                        });
                        form.setError("captchaToken", {
                          type: "manual",
                          message: "Captcha expired. Please try again",
                        });
                        recaptchaRef.current?.reset();
                      }}
                      onErrored={() => {
                        form.setValue("captchaToken", "", {
                          shouldValidate: true,
                        });
                        form.setError("captchaToken", {
                          type: "manual",
                          message: "Captcha error. Please try again",
                        });
                        recaptchaRef.current?.reset();
                      }}
                      theme="light"
                    />
                  </Suspense>
                </div>
                <motion.button
                  type="button"
                  onClick={nextStep}
                  disabled={form.formState.isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {form.formState.isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <span>
                      {currentStep === STEPS.length - 1
                        ? "Submit Booking"
                        : "Continue"}
                    </span>
                  )}
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      </main>

      <Suspense fallback={null}>
        <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
      </Suspense>

      <AnimatePresence>
        {showInstructorInfoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
            >
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">Instructor Request</h2>
                <button
                  onClick={() => {
                    setShowInstructorInfoModal(false);
                    setCurrentStep(currentStep + 1);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <p className="text-gray-600 mb-6 text-lg">
                  If you would like more than one instructor for your group, please write in the comments what you require.
                </p>

                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => {
                      setShowInstructorInfoModal(false);
                      setTimeout(() => {
                        document.getElementById('comments-section')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Take me to Comments
                  </button>
                  <button
                    onClick={() => {
                      setShowInstructorInfoModal(false);
                      setCurrentStep(currentStep + 1);
                    }}
                    className="w-full px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Continue to Review
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookingForm;
